import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@artosera/ui';
import { obtenirConnecteurs, obtenirSso } from '../../lib/api.js';
import { formaterDate } from '../../lib/format.js';
import pageStyles from '../../styles/page.module.css';
import tableStyles from '../../styles/table.module.css';
import styles from './ConnecteursTab.module.css';

export function ConnecteursTab({ idGalerie }: { idGalerie: string }): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const sso = useQuery({
    queryKey: ['console', 'galerie', idGalerie, 'sso'],
    queryFn: () => obtenirSso(idGalerie),
  });
  const requete = useQuery({
    queryKey: ['console', 'galerie', idGalerie, 'connecteurs'],
    queryFn: () => obtenirConnecteurs(idGalerie),
  });
  if (requete.isLoading) return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  if (requete.isError || !requete.data)
    return <p className={pageStyles.videEtat}>{t('connecteurs.erreur')}</p>;
  return (
    <section aria-labelledby="titre-connecteurs">
      <h2 id="titre-connecteurs">{t('connecteurs.titre')}</h2>
      <p>{requete.data.simulation ? t('connecteurs.simulation') : t('connecteurs.reel')}</p>
      <table className={`${tableStyles.table} ${styles.table}`}>
        <thead>
          <tr>
            <th scope="col">{t('connecteurs.colonnes.connecteur')}</th>
            <th scope="col">{t('connecteurs.colonnes.module')}</th>
            <th scope="col">{t('connecteurs.colonnes.etat')}</th>
            <th scope="col">{t('connecteurs.colonnes.secret')}</th>
            <th scope="col">{t('connecteurs.colonnes.verifie')}</th>
          </tr>
        </thead>
        <tbody>
          {requete.data.connecteurs.map((connecteur) => (
            <tr key={connecteur.id}>
              <th scope="row">{t(`connecteurs.noms.${connecteur.id}`)}</th>
              <td data-label={t('connecteurs.colonnes.module')}>
                {connecteur.moduleActif
                  ? t('connecteurs.moduleActif')
                  : t('connecteurs.moduleInactif')}
              </td>
              <td data-label={t('connecteurs.colonnes.etat')}>
                <Badge
                  tone={
                    connecteur.erreur ? 'withdrawn' : connecteur.configure ? 'available' : 'neutral'
                  }
                >
                  {connecteur.erreur
                    ? t('connecteurs.etats.erreur', { code: connecteur.erreur })
                    : connecteur.configure
                      ? t(`connecteurs.modes.${connecteur.mode}`)
                      : t('connecteurs.etats.nonConfigure')}
                </Badge>
              </td>
              <td data-label={t('connecteurs.colonnes.secret')}>
                {connecteur.finSecret ? `•••• ${connecteur.finSecret}` : '—'}
              </td>
              <td data-label={t('connecteurs.colonnes.verifie')}>
                {connecteur.verifieLe ? formaterDate(connecteur.verifieLe, '—') : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sso.isError && <p role="alert">{t('connecteurs.sso.erreur')}</p>}
      {sso.data && (
        <>
          {!sso.data.moduleActif && <p>{t('connecteurs.sso.inactif')}</p>}
          <p>
            {t('connecteurs.sso.resume', {
              identites: t('connecteurs.sso.identites', { count: sso.data.identitesLiees }),
              sessions: t('connecteurs.sso.sessions', { count: sso.data.sessionsOuvertes }),
              date: sso.data.derniereConnexionLe
                ? new Date(sso.data.derniereConnexionLe).toLocaleString(
                    i18n.resolvedLanguage === 'en' ? 'en-GB' : 'fr-FR',
                  )
                : t('connecteurs.sso.jamais'),
            })}
          </p>
        </>
      )}
    </section>
  );
}
